/**
 * Member-facing error presentation policy (UX-05-B).
 * Known product keys → localized message via resolveApiErrorMessage path.
 * Unknown / unsafe / internal-looking text → generic localized fallback.
 * RAW_BACKEND_MESSAGE_AS_DEFAULT_UI=NO
 */

const UNSAFE_MESSAGE_PATTERN = /sql(?:exception)?|nullpointerexception|stack\s*trace|at\s+[\w.$]+\(|\.java:\d+|hibernate|jdbc|org\.springframework|com\.silver\.|com\.heps\.|\/home\/|\/var\/|\/usr\/|table\s+[`'"]?\w+|column\s+[`'"]?\w+|select\s+.+\s+from\s+|update\s+\w+\s+set\s+|insert\s+into\s+|delete\s+from\s+|nacos|redis|mongodb?|authorization\s*[:=]|api[_-]?key|bearer\s+[a-z0-9._-]+|https?:\/\/internal\b/i;

/**
 * @param {unknown} message
 * @returns {boolean}
 */
export function isUnsafeInternalErrorMessage(message) {
  const text = String(message || '').trim();
  if (!text) return false;
  if (UNSAFE_MESSAGE_PATTERN.test(text)) return true;
  if (text.includes('\n\tat ') || text.includes('\n    at ')) return true;
  return false;
}

/**
 * Prefer localized product message for known keys; never surface raw/unsafe backend text.
 *
 * @param {import('../api/createClient').ApiError|Error|null|undefined} cause
 * @param {(key: string) => string} t
 * @param {string} fallbackKey
 * @param {{ preferBackendMessageForKeys?: Set<string> }} [options]
 */
export function resolveSafeUserVisibleError(
  cause,
  t,
  fallbackKey = 'travel.common.actionFailed',
  options = {},
) {
  const preferBackendKeys = options.preferBackendMessageForKeys || new Set([
    'travel.grouptrip.invalid.lifecycle',
    'travel.trade.conflict',
    'travel.grouptrip.concurrent.modification',
  ]);

  const backendKey = cause?.raw?.key;
  if (typeof backendKey === 'string' && backendKey.length > 0 && typeof t === 'function') {
    const translated = t(backendKey);
    if (typeof translated === 'string' && translated.length > 0 && translated !== backendKey) {
      return translated;
    }
    if (preferBackendKeys.has(backendKey) && cause?.message && !isUnsafeInternalErrorMessage(cause.message)) {
      return String(cause.message);
    }
  }

  const fallback = typeof t === 'function' ? t(fallbackKey) : '';
  if (typeof fallback === 'string' && fallback.length > 0 && fallback !== fallbackKey) {
    return fallback;
  }
  return '操作失敗，請稍後再試。';
}
